import type {
    SketchParametricSplineCurveDescriptor,
    SketchParametricSplineDifferentialEvaluation,
    SketchParametricSplinePointEvaluation,
} from '../../../../types/sketch/solver/SketchParametricTypes';
import {SKETCH_PARAMETRIC_TOLERANCE} from './SketchParametricToleranceConfig';

/**
 * 参数系统使用的二维有理 Bezier/B 样条求值器。
 *
 * 本类只读取 plain-data 控制点参数，不依赖 Three.js、Worker 或运行时草图对象。
 * 工作数组按最大次数复用，避免 LM 迭代热路径反复分配临时数组。
 */
export class SketchParametricSplineEvaluator {
    /** 齐次 x 工作数组。 */
    private readonly workingX: number[] = [];

    /** 齐次 y 工作数组。 */
    private readonly workingY: number[] = [];

    /** 齐次权重工作数组。 */
    private readonly workingWeight: number[] = [];

    /** 导数左采样点。 */
    private readonly derivativeLeft: SketchParametricSplinePointEvaluation = {x: 0, y: 0};

    /** 导数右采样点。 */
    private readonly derivativeRight: SketchParametricSplinePointEvaluation = {x: 0, y: 0};

    /**
     * 构造开放均匀 B 样条的完整节点向量。
     *
     * @param pointCount 控制点数量。
     * @param degree B 样条次数。
     * @returns 定义域为 `[0, 1]` 的夹持节点向量。
     */
    public static buildClampedUniformKnots(pointCount: number, degree: number): readonly number[] {
        const lastControlPointIndex = pointCount - 1;
        const resolvedDegree = Math.max(1, Math.min(Math.floor(degree), lastControlPointIndex));
        const knotLastIndex = lastControlPointIndex + resolvedDegree + 1;
        const knots = new Array<number>(knotLastIndex + 1).fill(0);

        for (let index = knotLastIndex - resolvedDegree; index <= knotLastIndex; index += 1) {
            knots[index] = 1;
        }

        const interiorCount = lastControlPointIndex - resolvedDegree;

        for (let index = 1; index <= interiorCount; index += 1) {
            knots[resolvedDegree + index] = index / (interiorCount + 1);
        }

        return knots;
    }

    /**
     * 求值曲线点。
     *
     * @param read 参数读取函数。
     * @param curve 样条曲线描述符。
     * @param parameter 曲线参数。
     * @param target 复用的输出对象。
     * @returns 写入后的输出对象。
     */
    public evaluatePoint(
        read: (parameterId: string) => number,
        curve: SketchParametricSplineCurveDescriptor,
        parameter: number,
        target: SketchParametricSplinePointEvaluation,
    ): SketchParametricSplinePointEvaluation {
        const clampedParameter = Math.max(curve.firstParameter, Math.min(curve.lastParameter, parameter));

        return curve.kind === 'bezier'
            ? this.evaluateBezier(read, curve, clampedParameter, target)
            : this.evaluateBSpline(read, curve, clampedParameter, target);
    }

    /**
     * 同时求值曲线点和一阶导数。
     *
     * 导数步长由本模块统一管理，禁止各关系公式自行选择数值差分尺度。
     *
     * @param read 参数读取函数。
     * @param curve 样条曲线描述符。
     * @param parameter 曲线参数。
     * @param target 复用的输出对象。
     * @returns 写入后的输出对象。
     */
    public evaluateDifferential(
        read: (parameterId: string) => number,
        curve: SketchParametricSplineCurveDescriptor,
        parameter: number,
        target: SketchParametricSplineDifferentialEvaluation,
    ): SketchParametricSplineDifferentialEvaluation {
        const span = curve.lastParameter - curve.firstParameter;

        if (!Number.isFinite(span) || span <= Number.EPSILON) {
            throw new Error('样条参数区间退化');
        }

        const step = Math.max(
            span * SKETCH_PARAMETRIC_TOLERANCE.splineDerivativeRelativeStep,
            SKETCH_PARAMETRIC_TOLERANCE.splineDerivativeMinimumStep,
        );
        const centerParameter = Math.max(curve.firstParameter, Math.min(curve.lastParameter, parameter));
        const leftParameter = Math.max(curve.firstParameter, centerParameter - step);
        const rightParameter = Math.min(curve.lastParameter, centerParameter + step);

        this.evaluatePoint(read, curve, centerParameter, target);
        this.evaluatePoint(read, curve, leftParameter, this.derivativeLeft);
        this.evaluatePoint(read, curve, rightParameter, this.derivativeRight);

        const denominator = rightParameter - leftParameter;

        if (denominator <= Number.EPSILON) {
            throw new Error('样条导数参数步长退化');
        }

        target.tangentX = (this.derivativeRight.x - this.derivativeLeft.x) / denominator;
        target.tangentY = (this.derivativeRight.y - this.derivativeLeft.y) / denominator;

        return target;
    }

    /**
     * 求值有理 Bezier 曲线。
     *
     * @param read 参数读取函数。
     * @param curve 曲线描述符。
     * @param parameter 曲线参数。
     * @param target 输出对象。
     * @returns 输出对象。
     */
    private evaluateBezier(
        read: (parameterId: string) => number,
        curve: SketchParametricSplineCurveDescriptor,
        parameter: number,
        target: SketchParametricSplinePointEvaluation,
    ): SketchParametricSplinePointEvaluation {
        const normalizedParameter = (parameter - curve.firstParameter)
            / (curve.lastParameter - curve.firstParameter);

        this.initializeWorkingPoints(read, curve, 0, curve.controlPoints.length - 1);

        for (let step = 1; step < curve.controlPoints.length; step += 1) {
            const remainingCount = curve.controlPoints.length - step;

            for (let index = 0; index < remainingCount; index += 1) {
                this.interpolateWorkingPoint(index, index + 1, normalizedParameter);
            }
        }

        return this.writeWorkingPoint(0, target);
    }

    /**
     * 求值有理 B 样条曲线。
     *
     * @param read 参数读取函数。
     * @param curve 曲线描述符。
     * @param parameter 曲线参数。
     * @param target 输出对象。
     * @returns 输出对象。
     */
    private evaluateBSpline(
        read: (parameterId: string) => number,
        curve: SketchParametricSplineCurveDescriptor,
        parameter: number,
        target: SketchParametricSplinePointEvaluation,
    ): SketchParametricSplinePointEvaluation {
        const lastControlPointIndex = curve.controlPoints.length - 1;
        const degree = Math.max(1, Math.min(Math.floor(curve.degree), lastControlPointIndex));
        const span = this.findKnotSpan(lastControlPointIndex, degree, parameter, curve.knots);

        this.initializeWorkingPoints(read, curve, span - degree, span);

        for (let step = 1; step <= degree; step += 1) {
            for (let index = degree; index >= step; index -= 1) {
                const knotIndex = span - degree + index;
                const denominator = curve.knots[knotIndex + degree - step + 1]! - curve.knots[knotIndex]!;
                const alpha = Math.abs(denominator) <= Number.EPSILON
                    ? 0
                    : (parameter - curve.knots[knotIndex]!) / denominator;

                this.interpolateWorkingPoint(index, index - 1, 1 - alpha);
            }
        }

        return this.writeWorkingPoint(degree, target);
    }

    /**
     * 把一段控制点写入齐次工作数组。
     *
     * @param read 参数读取函数。
     * @param curve 曲线描述符。
     * @param firstControlPointIndex 起始控制点索引。
     * @param lastControlPointIndex 终止控制点索引。
     */
    private initializeWorkingPoints(
        read: (parameterId: string) => number,
        curve: SketchParametricSplineCurveDescriptor,
        firstControlPointIndex: number,
        lastControlPointIndex: number,
    ): void {
        const requiredLength = lastControlPointIndex - firstControlPointIndex + 1;

        this.ensureWorkingCapacity(requiredLength);
        for (let index = 0; index < requiredLength; index += 1) {
            const controlPointIndex = firstControlPointIndex + index;
            const handle = curve.controlPoints[controlPointIndex]!;
            const weight = curve.weights[controlPointIndex] ?? 1;

            this.workingX[index] = read(handle.xId) * weight;
            this.workingY[index] = read(handle.yId) * weight;
            this.workingWeight[index] = weight;
        }
    }

    /**
     * 扩充工作数组容量。
     *
     * @param requiredLength 所需长度。
     */
    private ensureWorkingCapacity(requiredLength: number): void {
        while (this.workingX.length < requiredLength) {
            this.workingX.push(0);
            this.workingY.push(0);
            this.workingWeight.push(0);
        }
    }

    /**
     * 在齐次工作点之间执行线性插值。
     *
     * @param targetIndex 目标工作点索引。
     * @param sourceIndex 来源工作点索引。
     * @param sourceRatio 来源点比例。
     */
    private interpolateWorkingPoint(targetIndex: number, sourceIndex: number, sourceRatio: number): void {
        const targetRatio = 1 - sourceRatio;

        this.workingX[targetIndex] = targetRatio * this.workingX[targetIndex]!
            + sourceRatio * this.workingX[sourceIndex]!;
        this.workingY[targetIndex] = targetRatio * this.workingY[targetIndex]!
            + sourceRatio * this.workingY[sourceIndex]!;
        this.workingWeight[targetIndex] = targetRatio * this.workingWeight[targetIndex]!
            + sourceRatio * this.workingWeight[sourceIndex]!;
    }

    /**
     * 把齐次工作点转换为二维笛卡尔坐标。
     *
     * @param index 工作点索引。
     * @param target 输出对象。
     * @returns 输出对象。
     */
    private writeWorkingPoint(
        index: number,
        target: SketchParametricSplinePointEvaluation,
    ): SketchParametricSplinePointEvaluation {
        const weight = this.workingWeight[index]!;

        if (Math.abs(weight) <= Number.EPSILON) {
            target.x = this.workingX[index]!;
            target.y = this.workingY[index]!;
            return target;
        }

        target.x = this.workingX[index]! / weight;
        target.y = this.workingY[index]! / weight;
        return target;
    }

    /**
     * 查找参数所在的 B 样条节点区间。
     *
     * @param lastControlPointIndex 末控制点索引。
     * @param degree B 样条次数。
     * @param parameter 曲线参数。
     * @param knots 完整节点向量。
     * @returns 节点区间索引。
     */
    private findKnotSpan(
        lastControlPointIndex: number,
        degree: number,
        parameter: number,
        knots: readonly number[],
    ): number {
        if (parameter >= knots[lastControlPointIndex + 1]!) {
            return lastControlPointIndex;
        }
        if (parameter <= knots[degree]!) {
            return degree;
        }

        let low = degree;
        let high = lastControlPointIndex + 1;
        let middle = Math.floor((low + high) / 2);

        while (parameter < knots[middle]! || parameter >= knots[middle + 1]!) {
            if (parameter < knots[middle]!) {
                high = middle;
            } else {
                low = middle;
            }
            middle = Math.floor((low + high) / 2);
        }

        return middle;
    }
}
